/* Copyright (c) 2020-present CloudIO Inc */

import React, { CSSProperties, memo, useEffect, useMemo } from 'react';
import Cell from './Cell';
import { ColumnData, MakeCellProps } from './types';
import {
  useStateXAtomSetter,
  useStateXAtomValue,
  useStateXGetter,
  useStateXSetter,
} from '@cloudio-saas/statex';
import {
  dataGridCopyData,
  dataGridSelectedData,
  dataGridSelectedPath,
} from 'layout/Sidebar/atoms';
import { useCurrentDataStore } from 'components/DataStoreReference/DataStoreReference';
import {
  DataRecord,
  IoDatasourceAttributes,
} from '@cloudio-saas/datasource-types';
import { useParentPropsOfType, usePlatform } from 'platform/PlatformHooks';
import { processChildTriggers } from 'triggers/Triggers';
import { isEmpty } from 'utils/isEmpty';
import { isValid, format as formatDate } from 'date-fns';
import { validate as numberColumnValidate } from '../columns/NumberColumn/NumberColumn';
import { validate as stringColumnValidate } from '../columns/StringColumn/StringColumn';
import { AnyColumn } from 'componentregistry';
import { SwitchColumnCaseItem } from 'componentregistry/components/DataGrid/columns/SwitchColumn/SwitchColumnCase';
import { useFocusCell } from '../Context';

function CellWrapper(props: MakeCellProps): React.ReactElement {
  const {
    CellComponent,
    appUid,
    roleUid,
    className,
    columnIndex,
    columns,
    custom,
    dataContext,
    direction = 'ltr',
    header,
    highlightCell,
    highlightRow,
    left,
    onClick,
    onKeyDown,
    pageId,
    region,
    resetScroll,
    rowHeight,
    rowIndex,
    showColumnLines,
    showRowLines,
    width,
    columnData,
    updateSelectedCells,
    colData,
  } = props;

  const style = useMemo((): CSSProperties => {
    return {
      top: 0,
      height: rowHeight,
      width,
      [direction === 'rtl' ? 'right' : 'left']: left,
      lineHeight: `${rowHeight - 4 - 1}px`,
      position: 'absolute',
    };
  }, [direction, left, rowHeight, width]);

  const { dragFromCell, dragToCell, dragging } =
    useStateXAtomValue(dataGridSelectedPath);
  const value = columnData?.value;
  const setdataGridSelectedPath = useStateXAtomSetter(dataGridSelectedPath);
  const platform = usePlatform();
  const setdataGridCopyDate = useStateXAtomSetter(dataGridCopyData);
  const store = useCurrentDataStore<DataRecord>();
  const set = useStateXSetter();
  const get = useStateXGetter();
  const dataMap = get(dataGridSelectedData);
  const focusSignal = useFocusCell();

  const gridProps = useParentPropsOfType<'DataGrid'>({
    appUid,
    pageId,
    itemId: columnData?.itemId,
    filter: (p) => p.type === 'DataGrid',
  });

  const enableSpreadSheetCopy = gridProps?.enableSpreadSheetCopy ?? false;
  const allowHotKeyCopyPaste = gridProps?.allowHotKeyCopyPaste ?? false;
  const allowInsertOnPaste = gridProps?.allowInsertOnPaste ?? false;

  const isInsertable = (rowDataIndex: number, columnRowData: ColumnData) => {
    if (store) {
      const storeInsertable = store.isInsertable();
      const { insertable = { value: false, type: 'boolean' } } =
        columnRowData.column.props;
      const insertAllowed =
        storeInsertable &&
        (insertable.type === 'boolean'
          ? insertable.value
          : platform.evalBoolean(
              appUid,
              pageId,
              columnData?.column?.itemId,
              'insertable',
              {
                rowDataIndex,
                dsAlias: store.alias,
              },
              insertable.value,
            ));
      return insertAllowed;
    }
    return false;
  };

  const isReadOnly = (rowDataIndex: number, columnRowData: ColumnData) => {
    if (store) {
      const storeUpdatable = store.isUpdatable();
      const { updatable = { value: false, type: 'boolean' } } =
        columnRowData.column.props;
      const updateAllowed =
        storeUpdatable &&
        (updatable.type === 'boolean'
          ? updatable.value
          : platform.evalBoolean(
              appUid,
              pageId,
              columnData?.column?.itemId,
              'updatable',
              {
                rowDataIndex,
                dsAlias: store.alias,
              },
              updatable.value,
            ));
      return updateAllowed;
    }
    return false;
  };

  const currentCellUpdatable = isReadOnly(rowIndex, columnData);

  // const column = columns[columnIndex];
  // const { viewAttribute } = column.props;
  // useAudit({ appUid, pageId, active: editing && currentRow, viewAttribute });
  // console.log('CellWrapper', { rowIndex, columnIndex });

  let selected = false;
  let selectedLeftEdge = false;
  let selectedRightEdge = false;
  let selectedTopEdge = false;
  let selectedBottomEdge = false;
  let maxColumn;
  let maxRow;

  if (dragFromCell && dragToCell) {
    const minColumnIndex = Math.min(
      dragFromCell.columnIndex,
      dragToCell.columnIndex,
    );
    let minRowIndex = Math.min(dragFromCell.rowIndex, dragToCell.rowIndex);
    const maxColumnIndex = Math.max(
      dragFromCell.columnIndex,
      dragToCell.columnIndex,
    );
    if (minRowIndex < dataContext.headerRows) {
      minRowIndex = dataContext.headerRows;
    }
    const maxRowIndex = Math.max(dragFromCell.rowIndex, dragToCell.rowIndex);
    maxColumn = maxColumnIndex;
    maxRow = maxRowIndex;
    selected =
      minColumnIndex <= columnIndex &&
      minRowIndex <= rowIndex &&
      maxColumnIndex >= columnIndex &&
      maxRowIndex >= rowIndex &&
      currentCellUpdatable &&
      (enableSpreadSheetCopy || allowHotKeyCopyPaste);
    selectedLeftEdge =
      minColumnIndex <= columnIndex &&
      minColumnIndex === columnIndex &&
      maxRowIndex >= rowIndex &&
      minRowIndex <= rowIndex &&
      currentCellUpdatable &&
      (enableSpreadSheetCopy || allowHotKeyCopyPaste);
    selectedRightEdge =
      maxColumnIndex >= columnIndex &&
      maxColumnIndex === columnIndex &&
      maxRowIndex >= rowIndex &&
      minRowIndex <= rowIndex &&
      currentCellUpdatable &&
      (enableSpreadSheetCopy || allowHotKeyCopyPaste);
    selectedTopEdge = selected && minRowIndex === rowIndex;
    selectedBottomEdge = selected && maxRowIndex === rowIndex;
  }

  function copyToClipboard(content: string) {
    if (window.isSecureContext && navigator.clipboard) {
      navigator.clipboard.writeText(content);
    } else {
      unsecuredCopyToClipboard(content);
    }
  }

  function unsecuredCopyToClipboard(text: string) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
    } catch (err) {
      console.error('Unable to copy to clipboard, ' + err);
    }
    document.body.removeChild(textArea);
  }

  const handleCopy = () => {
    const selectedCells = get(dataGridSelectedData);
    let row, col;
    let gridCopyText = '';
    const minColumnIndex = Math.min(
      dragFromCell.columnIndex,
      dragToCell.columnIndex,
    );
    const minRowIndex = Math.min(dragFromCell.rowIndex, dragToCell.rowIndex);
    const maxColumnIndex =
      Math.max(dragFromCell.columnIndex, dragToCell.columnIndex) - 1;
    const maxRowIndex = Math.max(dragFromCell.rowIndex, dragToCell.rowIndex);
    if (selectedCells) {
      for (row = minRowIndex; row <= maxRowIndex; row++) {
        for (col = minColumnIndex; col <= maxColumnIndex; col++) {
          gridCopyText += `${selectedCells.get(`${row}${col}`)}\t`;
        }
        row !== maxRowIndex
          ? (gridCopyText += `${selectedCells.get(`${row}${col}`)}\n`)
          : (gridCopyText += `${selectedCells.get(`${row}${col}`)}`);
      }
    }
    copyToClipboard(gridCopyText);
    return gridCopyText;
  };

  const handleExcelPaste = async () => {
    const copyData = get(dataGridCopyData);
    const selectionData = get(dataGridSelectedPath);
    const textPasted = copyData.copyData;
    if (textPasted) {
      const rowPasted = textPasted
        .replace(
          /"((?:[^"]*(?:\r\n|\n\r|\n|\r))+[^"]+)"/gm,
          function (_match, p1) {
            return p1.replace(/""/g, '"').replace(/\r\n|\n\r|\n|\r/g, ' ');
          },
        )
        .split(/\r\n|\n\r|\n|\r/g)
        .map((row) => row.split('\t'));
      if (rowPasted && copyData.copyDragFrom && copyData.copyDragTo && store) {
        const rowDataIndex = copyData.copyDragTo?.rowIndex;
        const columnDataIndex = copyData.copyDragFrom?.columnIndex;
        const maxRowIndex = selectionData.dragToCell?.rowIndex;
        const maxColumnIndex = selectionData.dragToCell?.columnIndex;
        let rowValue = 0;
        let colValue = 0;
        for (let row = rowDataIndex; row <= maxRowIndex - 1; row++) {
          const recordId = store.getRecordIdAtRow(
            row - (dataContext.headerRows - 1),
          );
          if (!recordId) {
            return true;
          }
          for (let col = columnDataIndex; col <= maxColumnIndex; col++) {
            if (rowValue >= rowPasted.length) {
              rowValue = 0;
            }
            if (colValue >= rowPasted[0].length) {
              colValue = 0;
            }
            const updatedValue = rowPasted[rowValue][colValue];
            const column = colData[col];
            const viewAttribute = column.viewAttribute;
            const updateable = isReadOnly(rowIndex, column);
            if (viewAttribute && updateable) {
              const columnType = column.column.type;
              if (
                columnType === 'NumberColumn' ||
                columnType === 'StringColumn'
              ) {
                updateDefaultColumnRecord(
                  column.column,
                  updatedValue,
                  recordId,
                  viewAttribute,
                );
              } else {
                store.updateRecord(recordId, {
                  [viewAttribute]:
                    updatedValue === 'undefined' ? null : updatedValue,
                });
              }
              await processChildTriggers(
                {
                  dsAlias: store?.alias,
                  rowIndex: row - (dataContext.headerRows || 0) + 1,
                  triggerItemId: column.itemId,
                  appUid: appUid,
                  roleUid: roleUid,
                  itemId: column.itemId,
                  pageId: pageId,
                  platform: platform,
                },
                'TriggerOnChange',
              );
            }
            colValue++;
          }
          rowValue++;
        }
        setdataGridSelectedPath({
          dragFromCell: dragToCell,
          dragToCell: dragToCell,
          dragging: false,
        });
        setdataGridCopyDate({
          copyData: null,
          copyDragFrom: dragToCell,
          copyDragTo: dragToCell,
          eventType: undefined,
        });
      }
    }
  };

  const onMouseKeyDown = (
    e: React.MouseEvent<HTMLDivElement>,
    eventType?: string,
  ) => {
    if (eventType !== 'EXCEL') {
      setdataGridSelectedPath({
        dragFromCell: { rowIndex, columnIndex },
        dragToCell,
        dragging: true,
      });
      const newSelectedCells: Map<string, string | undefined | null> =
        new Map();
      set(dataGridSelectedData, newSelectedCells);
    } else {
      const copyData = handleCopy();
      setdataGridSelectedPath({
        dragFromCell: dragFromCell,
        dragToCell,
        dragging: true,
      });
      setdataGridCopyDate({
        copyData: copyData,
        copyDragFrom: dragFromCell,
        copyDragTo: { rowIndex, columnIndex },
        eventType: eventType,
      });
    }
  };

  const updateDefaultColumnRecord = async (
    currentColumn: AnyColumn,
    updatedValue: string,
    updatedRecordId: string,
    viewAttribute: string,
  ) => {
    const columnType = currentColumn.type;
    if (store && viewAttribute) {
      if (columnType === 'NumberColumn') {
        const num = parseFloat(updatedValue);
        const errMsg = isEmpty(num)
          ? undefined
          : numberColumnValidate(currentColumn, num);
        if (errMsg && updatedRecordId) {
          store?.setError(viewAttribute, updatedRecordId, errMsg);
        } else if (
          updatedRecordId &&
          store?.hasError(viewAttribute, updatedRecordId)
        ) {
          store?.clearError(viewAttribute, updatedRecordId);
        }
        store.updateRecord(updatedRecordId, {
          [viewAttribute]: updatedValue === 'undefined' ? null : num,
        });
      } else if (columnType === 'StringColumn') {
        const errMsg = stringColumnValidate(currentColumn.props, updatedValue);
        if (errMsg && updatedRecordId) {
          store?.setError(viewAttribute, updatedRecordId, errMsg);
        } else if (
          updatedRecordId &&
          store?.hasError(viewAttribute, updatedRecordId)
        ) {
          store?.clearError(viewAttribute, updatedRecordId);
        }
        store.updateRecord(updatedRecordId, {
          [viewAttribute]: updatedValue === 'undefined' ? null : updatedValue,
        });
      } else if (columnType === 'DateColumn') {
        let attr: IoDatasourceAttributes | undefined;
        if (viewAttribute) {
          attr = store.getAttribute(viewAttribute);
        }
        const isStringDateTime = attr?.attributeType === 'StringDateTime';
        const dateOnly = attr?.attributeType === 'Date';
        var date = new Date(updatedValue);
        const newValue =
          date && isValid(date)
            ? isStringDateTime
              ? formatDate(date, "yyyy-MM-dd'T'HH:mm:ss")
              : dateOnly
              ? formatDate(date, 'yyyy-MM-dd')
              : date.toISOString()
            : '';
        store.updateRecord(updatedRecordId, {
          [viewAttribute]: newValue === 'undefined' ? null : newValue,
        });
      } else if (columnType === 'LOVColumn' || columnType === 'SelectColumn') {
        store.updateRecord(updatedRecordId, {
          [viewAttribute]: updatedValue === 'undefined' ? null : updatedValue,
        });
      }
    }
  };

  const handlePaste = async (event: ClipboardEvent) => {
    event.preventDefault();
    event.stopPropagation();
    let row: number, col, updatedRecordIndex;
    const currentPastedRecords = [];
    const textPasted = event.clipboardData?.getData('text/plain');
    const selectionData = get(dataGridSelectedPath);
    if (textPasted) {
      document.removeEventListener('paste', handlePaste);
      const rowPasted = textPasted
        .replace(
          /"((?:[^"]*(?:\r\n|\n\r|\n|\r))+[^"]+)"/gm,
          function (_match, p1) {
            return p1.replace(/""/g, '"').replace(/\r\n|\n\r|\n|\r/g, ' ');
          },
        )
        .replace(/\r\n+$/, '')
        .split(/\r\n|\n\r|\n|\r/g)
        .map((s) => s.split('\t'));
      const maxRowIndex = selectionData.dragToCell?.rowIndex;
      const maxColumnIndex = selectionData.dragToCell?.columnIndex;
      const lovPastedRowsInfo = [];
      const selectedRowCount =
        maxRowIndex !== undefined ? maxRowIndex - rowIndex + 1 : 1;
      const selectedColumnCount =
        maxColumnIndex !== undefined ? maxColumnIndex - columnIndex + 1 : 1;
      const maxPastedColumns = Math.max(...rowPasted.map((r) => r.length), 0);
      let rowsToProcess: string[][];
      if (
        rowPasted.length < selectedRowCount ||
        maxPastedColumns < selectedColumnCount
      ) {
        rowsToProcess = [];
        for (let _row = 0; _row < selectedRowCount; _row++) {
          const originalRow = rowPasted[_row % rowPasted.length];
          const expandedRow: string[] = [];
          for (let _col = 0; _col < selectedColumnCount; _col++) {
            expandedRow.push(originalRow[_col % originalRow.length]);
          }
          rowsToProcess.push(expandedRow);
        }
      } else {
        rowsToProcess = rowPasted;
      }
      for (row = 0; row < rowsToProcess.length; row++) {
        let copyRecordId;
        const currentRowIndex = row + rowIndex;
        const recordId = store?.getRecordIdAtRow(
          row + (rowIndex - (dataContext.headerRows || 0)),
        );
        if (recordId === undefined) {
          if (store?.isInsertable()) {
            updatedRecordIndex = await store.createNew({
              partialRecord: {},
              addOnTop: false,
              rs: 'I',
            });
          }
        } else {
          const record = store?.getRecord(recordId);
          const isNew = record?._rs === 'I' || record?._rs === 'N';
          const hasData = Object.keys(record || {}).some(
            (k) => k !== '_cid' && k !== '_rs',
          );

          if (
            (isNew && hasData && allowInsertOnPaste && store?.isInsertable()) ||
            (!isNew && allowInsertOnPaste && store?.isInsertable())
          ) {
            updatedRecordIndex = await store?.createNew({
              partialRecord: {},
              addBefore: recordId,
              rs: 'I',
            });
          }
        }
        for (col = 0; col < rowsToProcess[row].length; col++) {
          const currentColIndex = col + columnIndex;
          const currentColumn = colData[currentColIndex];
          if (!currentColumn) {
            continue;
          }
          const updatedValue = rowsToProcess[row][col];
          const viewAttribute = currentColumn.viewAttribute;
          const columnType = currentColumn.column.type;
          const columnItemId = currentColumn.column.itemId;
          const updatedRecordId = updatedRecordIndex
            ? updatedRecordIndex
            : recordId;
          copyRecordId = updatedRecordId;
          const updateable = isReadOnly(rowIndex, currentColumn);
          const insertable = isInsertable(rowIndex, currentColumn);
          if (
            viewAttribute &&
            (allowInsertOnPaste && allowHotKeyCopyPaste
              ? insertable
              : updateable) &&
            store &&
            updatedRecordId
          ) {
            if (columnType === 'LOVColumn') {
              lovPastedRowsInfo.push({
                comboDatasourceAlias:
                  currentColumn?.column?.props?.comboDatasourceAlias,
                updatedValue,
                updatedRecordId,
                viewAttribute,
                itemId: columnItemId,
                returnValueAttribute:
                  currentColumn?.column?.props?.returnValueAttribute,
              });
            }
            if (
              columnType === 'NumberColumn' ||
              columnType === 'StringColumn' ||
              columnType === 'DateColumn' ||
              columnType === 'LOVColumn' ||
              columnType === 'SelectColumn'
            ) {
              updateDefaultColumnRecord(
                currentColumn.column,
                updatedValue,
                updatedRecordId,
                viewAttribute,
              );
            } else if (columnType === 'SwitchColumn') {
              const { expression } = currentColumn.column.props;
              const expResult = expression
                ? platform.evalIfExpressionString(
                    appUid,
                    pageId,
                    currentColumn.column.itemId,
                    'expression',
                    { rowIndex: row, dsAlias: store.alias },
                    expression,
                  )
                : '';

              const switchCases = platform.getChildrenItemsById(
                appUid,
                pageId,
                currentColumn.column.itemId,
                (child) => {
                  const caseValue = (child as SwitchColumnCaseItem).props.case;
                  const matchedChild =
                    child.type === 'SwitchColumnCase' &&
                    (!caseValue || caseValue === expResult);

                  if (matchedChild && (child.props.visible ?? true)) {
                    if (!child.props.visibleCondition) {
                      return true;
                    } else {
                      return platform.evalBoolean(
                        appUid,
                        pageId,
                        child.itemId,
                        'visibleCondition',
                        { rowIndex, dsAlias: store.alias },
                        child.props.visibleCondition,
                      );
                    }
                  }
                  return false;
                },
              ) as SwitchColumnCaseItem[];
              if (
                switchCases.length > 0 &&
                switchCases[0].children.length > 0
              ) {
                const columnCaseMd = switchCases[0];
                if (columnCaseMd) {
                  const c = platform.getItem(
                    appUid,
                    pageId,
                    columnCaseMd.children[0],
                  ) as AnyColumn;
                  const { viewAttribute: switchViewAttribute } = c?.props;
                  if (switchViewAttribute) {
                    updateDefaultColumnRecord(
                      c,
                      updatedValue,
                      updatedRecordId,
                      switchViewAttribute,
                    );
                  }
                }
              }
            }
            await processChildTriggers(
              {
                dsAlias: store?.alias,
                rowIndex: currentRowIndex - (dataContext.headerRows || 0),
                triggerItemId: currentColumn.itemId,
                appUid: appUid,
                roleUid: roleUid,
                itemId: currentColumn.itemId,
                pageId: pageId,
                platform: platform,
              },
              'TriggerOnChange',
            );
            await processChildTriggers(
              {
                dsAlias: store?.alias,
                rowIndex: row + rowIndex - (dataContext.headerRows || 0),
                triggerItemId: currentColumn.itemId,
                appUid: appUid,
                roleUid: roleUid,
                itemId: currentColumn.itemId,
                pageId: pageId,
                platform: platform,
              },
              'TriggerOnPaste',
            );
            if (recordId === undefined && store?.isInsertable()) {
              focusSignal.set((v) => ({
                ...v,
                focusRow: currentRowIndex,
                focusColumn: currentColIndex,
              }));
            }
          }
        }
        store &&
          copyRecordId &&
          currentPastedRecords.push(store.getRecord(copyRecordId));
      }
      const comboDSAliasGroupedData: Record<
        string,
        {
          returnValueAttribute: string;
          itemId: string;
          entries: {
            updatedValue: string;
            updatedRecordId: string;
          }[];
        }
      > = {}; // will contain column info and pasted value and group it based on the lov datasource alias
      lovPastedRowsInfo.forEach((lovRow) => {
        const alias = lovRow.comboDatasourceAlias;
        const returnValueAttribute = lovRow.returnValueAttribute;
        const itemId = lovRow.itemId;
        if (!comboDSAliasGroupedData[alias]) {
          comboDSAliasGroupedData[alias] = {
            returnValueAttribute,
            itemId,
            entries: [],
          };
        }
        comboDSAliasGroupedData[alias].entries.push({
          updatedValue: lovRow.updatedValue,
          updatedRecordId: lovRow.updatedRecordId,
        });
      });
      Object.entries(comboDSAliasGroupedData).forEach(
        async ([
          comboDatasourceAlias,
          { returnValueAttribute, itemId, entries },
        ]) => {
          const lovStore = platform.getStore<DataRecord>(
            appUid,
            pageId,
            comboDatasourceAlias,
          );
          if (lovStore && store) {
            const updatedValues = entries.map((e) => e.updatedValue);
            lovStore.setGridFilter([
              { [returnValueAttribute]: { in: updatedValues } },
            ]);
            await lovStore?.query();
            const records = lovStore.getRecords();
            entries.forEach(({ updatedValue, updatedRecordId }) => {
              const record = records.find(
                (r) => r[returnValueAttribute] === updatedValue,
              );
              const lovMappings = platform.getChildrenOfTypeById(
                appUid,
                pageId,
                itemId,
                'LOVMappings',
              );
              if (lovMappings.length) {
                const maps = platform.getChildrenOfTypeById(
                  appUid,
                  pageId,
                  lovMappings[0].itemId,
                  'LOVMap',
                );
                maps.forEach((map) => {
                  store.updateRecord(updatedRecordId, {
                    [map.props.targetAttribute]: record
                      ? record[map.props.sourceAttribute as string]
                      : null,
                  });
                });
              }
            });
          }
        },
      );
      await processChildTriggers(
        {
          dsAlias: store?.alias,
          records: currentPastedRecords,
          appUid: appUid,
          roleUid: roleUid,
          pageId: pageId,
          platform: platform,
          itemId: columnData?.itemId,
          triggerItemId: columnData?.itemId,
        },
        'TriggerAfterPaste',
      );
    }
  };

  const onKeyDownFn = (event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case 'c':
        if (
          (event.ctrlKey || event.metaKey) &&
          dragFromCell &&
          allowHotKeyCopyPaste
        ) {
          handleCopy();
          event.preventDefault();
          event.stopPropagation();
        }
        break;
      case 'v':
        if ((event.ctrlKey || event.metaKey) && allowHotKeyCopyPaste) {
          document.addEventListener('paste', handlePaste);
        }
        break;
      default:
    }
    if (onKeyDown) {
      onKeyDown(event);
    }
  };

  const onMouseKeyUp = (
    e: React.MouseEvent<HTMLDivElement>,
    eventType?: string,
  ) => {
    setdataGridSelectedPath({
      dragFromCell: dragFromCell,
      dragToCell: { rowIndex, columnIndex },
      dragging: false,
    });
    if (eventType === 'EXCEL') {
      handleExcelPaste();
    }
  };

  const onMouseKeyEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    if (dragging) {
      setdataGridSelectedPath({
        dragFromCell: dragFromCell,
        dragToCell: { rowIndex, columnIndex },
        dragging: true,
      });
      const newSelectedCells: Map<string, string | undefined | null> =
        new Map();
      set(dataGridSelectedData, newSelectedCells);
    }
  };

  const onMouseKeyLeave = (e: React.MouseEvent<HTMLDivElement>) => {};

  useEffect(() => {
    if (selected && updateSelectedCells) {
      updateSelectedCells(rowIndex, columnIndex, value);
    }
  }, [columnIndex, dataMap, rowIndex, selected, updateSelectedCells, value]);

  return (
    <Cell
      CellComponent={CellComponent}
      appUid={appUid}
      roleUid={roleUid}
      className={className}
      columnIndex={columnIndex}
      columns={columns}
      custom={custom}
      dataContext={dataContext}
      header={header}
      height={rowHeight - 4 - 2}
      highlightCell={highlightCell}
      highlightRow={highlightRow}
      key={`${region}-${columnIndex}x${rowIndex}`}
      onClick={onClick}
      onKeyDown={onKeyDownFn}
      onMouseKeyDown={onMouseKeyDown}
      onMouseKeyUp={onMouseKeyUp}
      onMouseKeyEnter={onMouseKeyEnter}
      onMouseKeyLeave={onMouseKeyLeave}
      pageId={pageId}
      region={region}
      resetScroll={resetScroll}
      rowIndex={rowIndex}
      showColumnLines={showColumnLines}
      showRowLines={showRowLines}
      style={style}
      width={width - 8 - 2}
      columnData={columnData}
      selectedLeftEdge={selectedLeftEdge}
      selectedRightEdge={selectedRightEdge}
      selectedTopEdge={selectedTopEdge}
      selectedBottomEdge={selectedBottomEdge}
      maxRow={maxRow}
      maxColumn={maxColumn}
      currentCellUpdatable={currentCellUpdatable}
      enableSpreadSheetCopy={enableSpreadSheetCopy}
    />
  );
}

export default memo(CellWrapper);
